import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  LEAD_CHANNELS, LEAD_CHANNEL_LABELS,
  LEAD_MEDIUMS, LEAD_MEDIUM_LABELS,
  LEAD_SOURCES, LEAD_SOURCE_LABELS,
} from '@troia-v3/types';
import { Drawer, Button, MoneyInput, Select, Textarea, Input } from '@/shared/components/ui';
import { useStepsByFunnelId, useFunnelsDropdown } from '@/modules/funnels';
import { useTeamsDropdown } from '@/modules/teams/hooks/useTeams';
import { useLeadsCampaignsDropdown, useLeadsAdsetsDropdown, useLeadsAdsDropdown, useLeadAssigneesDropdown } from '../hooks/useLeadsDropdowns';
import { useClearStaleOperator } from '@/shared/hooks/useClearStaleOperator';
import { useCreateLead } from '../hooks';
import { ContactSearchCombobox } from './ContactSearchCombobox';
import { AddContactDialog } from '@/modules/chat/components';
import type { ContactFormData } from '@/modules/chat/components';
import { useCreateContact, useContact, useUpdateContact } from '@/modules/chat/hooks/useContacts';
import EditContactDialog, { type EditContactFormData } from '@/modules/chat/components/EditContactDialog';
import type { ContactDropdownItem } from '@/modules/contacts/hooks/useContactsDropdown';
import { PopulatedLead } from '../types';
import { leadsService } from '../services/leadsService';
import { Icon } from '@/shared/icons'
import type { IconName } from '@/shared/icons'
// ═══════════════════════════════════════════════════════
// ENUM OPTIONS (PT-BR)
// ═══════════════════════════════════════════════════════

const PRIORITY_OPTIONS = [
  { value: 'low', label: 'Baixa' },
  { value: 'medium', label: 'Média' },
  { value: 'high', label: 'Alta' },
  { value: 'urgent', label: 'Urgente' },
];

const TEMPERATURE_OPTIONS = [
  { value: 'cold', label: 'Fria' },
  { value: 'warm', label: 'Morna' },
  { value: 'hot', label: 'Quente' },
];

const QUALIFY_STATUS_OPTIONS = [
  { value: 'pending', label: 'Pendente' },
  { value: 'qualified', label: 'Qualificado' },
  { value: 'disqualified', label: 'Desqualificado' },
];

// SOURCE_OPTIONS, MEDIUM_OPTIONS, CHANNEL_OPTIONS derivados dos enums canônicos
// em `@troia-v3/types/leads`. Adicionar nova source/medium/channel: editar
// `LEAD_SOURCES`/`LEAD_MEDIUMS`/`LEAD_CHANNELS` lá (single source of truth).
const SOURCE_OPTIONS = LEAD_SOURCES.map((s) => ({ value: s, label: LEAD_SOURCE_LABELS[s] }));
const MEDIUM_OPTIONS = LEAD_MEDIUMS.map((m) => ({ value: m, label: LEAD_MEDIUM_LABELS[m] }));

const SOURCE_CHANNEL_MAP: Record<string, { value: string; label: string }[]> = {
  meta: [
    { value: 'instagram', label: 'Instagram' },
    { value: 'facebook', label: 'Facebook' },
    { value: 'whatsapp', label: 'WhatsApp' },
    { value: 'messenger', label: 'Messenger' },
  ],
  google: [
    { value: 'google', label: 'Google' },
    { value: 'youtube', label: 'YouTube' },
  ],
  tiktok: [{ value: 'tiktok', label: 'TikTok' }],
  linkedin: [{ value: 'linkedin', label: 'LinkedIn' }],
  microsoft: [{ value: 'bing', label: 'Bing' }],
  organic: [
    { value: 'website', label: 'Website' },
    { value: 'other', label: 'Outro' },
  ],
  referral: [{ value: 'other', label: 'Outro' }],
  email: [{ value: 'email', label: 'Email' }],
  offline: [
    { value: 'phone', label: 'Telefone' },
    { value: 'other', label: 'Outro' },
  ],
  partner: [{ value: 'other', label: 'Outro' }],
  outbound: [
    { value: 'phone', label: 'Telefone' },
    { value: 'email', label: 'Email' },
    { value: 'whatsapp', label: 'WhatsApp' },
    { value: 'other', label: 'Outro' },
  ],
  direct: [
    { value: 'whatsapp', label: 'WhatsApp' },
    { value: 'instagram', label: 'Instagram' },
    { value: 'facebook', label: 'Facebook' },
    { value: 'telegram', label: 'Telegram' },
    { value: 'email', label: 'Email' },
    { value: 'website', label: 'Website' },
    { value: 'phone', label: 'Telefone' },
    { value: 'other', label: 'Outro' },
  ],
};

const CHANNEL_OPTIONS = LEAD_CHANNELS.map((c) => ({ value: c, label: LEAD_CHANNEL_LABELS[c] }));

// ═══════════════════════════════════════════════════════
// PROPS
// ═══════════════════════════════════════════════════════

export interface LeadFormDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  mode: 'create' | 'edit';
  lead?: PopulatedLead | null;
  onSuccess?: () => void;
  /** Contato pré-selecionado (ex: ao criar lead a partir do chat) */
  initialContact?: ContactDropdownItem | null;
}

// ═══════════════════════════════════════════════════════
// SEÇÕES (mockup lead-form.html: `.ld-sec` 18/20 + `.ld-sh` tile 30 + título 14 bold)
// ═══════════════════════════════════════════════════════

/** `.ld-sh`: tile 30×30 r8 sunken + título 14 bold tracking-tight + `.req` + chevron (colapsável) */
const SecH: React.FC<{
  title: string;
  icon: IconName;
  required?: boolean;
  collapsible?: boolean;
  open?: boolean;
  onToggle?: () => void;
}> = ({ title, icon, required, collapsible, open, onToggle }) => (
  <div
    className={`flex items-center gap-[11px] ${collapsible ? 'cursor-pointer select-none' : ''}`}
    style={{ marginBottom: collapsible && !open ? 0 : 14 }}
    onClick={collapsible ? onToggle : undefined}
    role={collapsible ? 'button' : undefined}
    data-testid={collapsible ? `lead-form-sec-${title.toLowerCase().replace(/\W+/g, '-')}` : undefined}
  >
    <div
      className="flex items-center justify-center flex-none"
      style={{ width: 30, height: 30, borderRadius: 8, backgroundColor: '#f1f5f9', color: '#475569' }}
    >
      <Icon name={icon} className="w-3.5 h-3.5" />
    </div>
    <h3 className="flex-1 font-bold text-sm tracking-[-0.02em]" style={{ color: '#1e293b' }}>
      {title}
      {required && <span style={{ color: '#ef4444', fontWeight: 700 }}> *</span>}
    </h3>
    {collapsible && (
      <Icon
        name="chevron-up"
        className="w-3.5 h-3.5 flex-none transition-transform"
        style={{ color: '#94a3b8', transform: open ? undefined : 'rotate(-90deg)' }}
      />
    )}
  </div>
);

/** `.ld-sec`: padding 18px 20px + divisor superior #e5e7eb (first = sem divisor) */
const Sec: React.FC<{ first?: boolean; children: React.ReactNode }> = ({ first, children }) => (
  <div style={{ padding: '18px 20px', borderTop: first ? undefined : '1px solid #e5e7eb' }}>
    {children}
  </div>
);

/** `.ld-sc`: corpo da seção — coluna gap 14 */
const SecBody: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="flex flex-col gap-3.5">{children}</div>
);

// ═══════════════════════════════════════════════════════
// COMPONENT
// ═══════════════════════════════════════════════════════

export const LeadFormDrawer: React.FC<LeadFormDrawerProps> = ({
  isOpen,
  onClose,
  mode,
  lead,
  onSuccess,
  initialContact,
}) => {
  const isEdit = mode === 'edit';

  // ─── Contact state (create mode only) ───
  const [selectedContact, setSelectedContact] = useState<ContactDropdownItem | null>(null);

  // ─── AddContactDialog state ───
  const [isAddContactOpen, setIsAddContactOpen] = useState(false);

  // ─── EditContactDialog state ───
  const [isEditContactOpen, setIsEditContactOpen] = useState(false);

  // ─── Form state ───
  const [selectedFunnelId, setSelectedFunnelId] = useState('');
  const [selectedStepId, setSelectedStepId] = useState('');
  const [priority, setPriority] = useState('');
  const [temperature, setTemperature] = useState('');
  const [qualifyStatus, setQualifyStatus] = useState('');
  const [channel, setChannel] = useState('');
  const [origin, setOrigin] = useState('');
  const [source, setSource] = useState('');
  const [medium, setMedium] = useState('');
  const [type, setType] = useState('');
  const [campaignName, setCampaignName] = useState('');
  const [adsetName, setAdsetName] = useState('');
  const [adName, setAdName] = useState('');
  const [assigneeId, setAssigneeId] = useState('');
  const [teamId, setTeamId] = useState('');
  const [budget, setBudget] = useState<number | undefined>(undefined);
  const [segment, setSegment] = useState('');
  const [description, setDescription] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  // ─── Seções colapsáveis (mockup lead-form: Atribuição/Classificação/Detalhes
  //     expandidas; Origem & Fonte fechada por default) ───
  const [originExpanded, setOriginExpanded] = useState(false);
  const [assignExpanded, setAssignExpanded] = useState(true);
  const [classifyExpanded, setClassifyExpanded] = useState(true);
  const [detailsExpanded, setDetailsExpanded] = useState(true);

  // Track se funnelId mudou (para evitar reset de step na primeira carga no edit)
  const [funnelIdInitialized, setFunnelIdInitialized] = useState(false);

  // ─── Hooks de dropdown ───
  const { data: funnelsData } = useFunnelsDropdown();
  const { data: steps } = useStepsByFunnelId(selectedFunnelId || undefined);
  // Responsável: só quem participa do funil escolhido (qualquer papel).
  const { data: assignableUsers } = useLeadAssigneesDropdown(selectedFunnelId || undefined, { enabled: isOpen });
  const { data: teamsData } = useTeamsDropdown();
  const { data: campaignsData } = useLeadsCampaignsDropdown();
  const { data: adsetsData } = useLeadsAdsetsDropdown();
  const { data: adsData } = useLeadsAdsDropdown();

  // ─── Contact ID para edição (create mode: contato selecionado; edit mode: contato do lead) ───
  const editContactId = isEditContactOpen
    ? (isEdit ? lead?.contactId : selectedContact?.id)
    : undefined;
  const { data: fullContact } = useContact(editContactId);

  // ─── Mutations ───
  const createLeadMutation = useCreateLead();
  const createContactMutation = useCreateContact();
  const updateContactMutation = useUpdateContact();

  // ─── Pré-preencher formulário quando lead mudar (edit mode) ───
  useEffect(() => {
    if (isEdit && lead) {
      setSelectedFunnelId(lead.funnelId || lead.funnel?.id || '');
      setSelectedStepId(lead.stepId || lead.step?.id || '');
      setPriority(lead.priority || '');
      setTemperature(lead.temperature || '');
      setQualifyStatus(lead.qualifyStatus || '');
      setChannel(lead.channel || '');
      setOrigin(lead.origin || '');
      setSource(lead.source || '');
      setMedium(lead.medium || '');
      setType(lead.type || '');
      setCampaignName(lead.campaignName || '');
      setAdsetName(lead.adsetName || '');
      setAdName(lead.adName || '');
      setAssigneeId(lead.assigneeId || lead.assignee?.id || '');
      setTeamId(lead.teamId || '');
      setBudget(lead.budget);
      setSegment(lead.segment || '');
      setDescription(lead.description || '');
      setFunnelIdInitialized(false);
    }
  }, [lead, isEdit]);

  // ─── Reset form ao abrir no create mode ───
  useEffect(() => {
    if (isOpen && !isEdit) {
      setSelectedContact(initialContact || null);
      setSelectedFunnelId('');
      setSelectedStepId('');
      setPriority('');
      setTemperature('');
      setQualifyStatus('');
      setChannel('');
      setOrigin('');
      setSource('');
      setMedium('');
      setType('');
      setCampaignName('');
      setAdsetName('');
      setAdName('');
      setAssigneeId('');
      setTeamId('');
      setBudget(undefined);
      setSegment('');
      setDescription('');
      setFunnelIdInitialized(false);
      setFieldErrors({});
      setOriginExpanded(false);
    }
  }, [isOpen, isEdit]);

  // Reset fieldErrors ao abrir no edit mode
  useEffect(() => {
    if (isOpen && isEdit) {
      setFieldErrors({});
    }
  }, [isOpen, isEdit]);

  // Ao fechar o drawer pai, fecha os diálogos-filho (Add/Edit contato) — senão o
  // estado persiste e reabrem "abertos" na próxima vez. Ver DS §Overlays (ciclo pai→filho).
  useEffect(() => {
    if (!isOpen) {
      setIsAddContactOpen(false);
      setIsEditContactOpen(false);
    }
  }, [isOpen]);

  // ─── Reset step quando mudar funnel (mas não na inicialização do edit) ───
  useEffect(() => {
    if (isEdit) {
      if (funnelIdInitialized) {
        setSelectedStepId('');
      } else if (selectedFunnelId) {
        setFunnelIdInitialized(true);
      }
    } else {
      // Create mode: sempre resetar step ao mudar funnel
      if (funnelIdInitialized) {
        setSelectedStepId('');
      } else if (selectedFunnelId) {
        setFunnelIdInitialized(true);
      }
    }
  }, [selectedFunnelId]);

  // Edição: o responsável gravado pode não participar do funil (dado antigo).
  // Enquanto o funil não muda, ele continua na lista — sem isso o campo
  // apareceria vazio e salvar apagaria o responsável sem ninguém pedir.
  const originalFunnelId = lead?.funnelId || lead?.funnel?.id || '';
  const originalAssigneeId = lead?.assigneeId || lead?.assignee?.id || '';
  const keepsOriginalAssignee =
    isEdit && !!originalAssigneeId && assigneeId === originalAssigneeId && selectedFunnelId === originalFunnelId;

  // Trocou o funil e o responsável escolhido não participa do novo → limpa.
  useClearStaleOperator(keepsOriginalAssignee ? null : (assigneeId || null), assignableUsers, () => setAssigneeId(''));

  // ─── Handler para criar contato via AddContactDialog ───
  const handleRequestCreateContact = (_prefillName: string) => {
    setIsAddContactOpen(true);
  };

  const handleSaveNewContact = (contactData: ContactFormData, avatar?: File) => {
    createContactMutation.mutate(
      { data: contactData, avatar },
      {
        onSuccess: (newContact: any) => {
          // Selecionar o contato recém-criado no combobox
          const contactItem: ContactDropdownItem = {
            id: newContact.id,
            name: newContact.name,
            email: newContact.identifiers?.email?.[0] || undefined,
            phone: newContact.identifiers?.phone?.[0] || undefined,
          };
          setSelectedContact(contactItem);
          setIsAddContactOpen(false);
          toast.success('Contato criado com sucesso!');
        },
        onError: (error: any) => {
          if (error.response?.status === 402 || error.response?.status === 403) return;
          toast.error('Erro ao criar contato. Tente novamente.');
        },
      }
    );
  };

  // ─── Handler para editar contato via EditContactDialog ───
  const handleUpdateContact = async (contactId: string, data: EditContactFormData, avatar?: File) => {
    try {
      await updateContactMutation.mutateAsync({ id: contactId, data, avatar });
      toast.success('Contato atualizado com sucesso!');
      setIsEditContactOpen(false);
    } catch {
      toast.error('Erro ao atualizar contato. Tente novamente.');
    }
  };

  // ─── Mapeamento campo → seção (para expandir automaticamente) ───
  const FIELD_SECTION_MAP: Record<string, 'assignment' | 'classification' | 'details' | 'origin'> = {
    assigneeId: 'assignment',
    teamId: 'assignment',
    priority: 'classification',
    temperature: 'classification',
    qualifyStatus: 'classification',
    budget: 'details',
    segment: 'details',
    description: 'details',
    channel: 'origin',
    origin: 'origin',
    source: 'origin',
    campaignName: 'origin',
    adsetName: 'origin',
    adName: 'origin',
  };

  // ─── Parsear erros de validação do backend ───
  const parseBackendErrors = (error: unknown): boolean => {
    if (!axios.isAxiosError(error)) return false;

    const data = error.response?.data;
    if (!data?.errors || typeof data.errors !== 'object') return false;

    const parsed: Record<string, string> = {};
    const sectionsToExpand = new Set<string>();

    for (const [field, messages] of Object.entries(data.errors)) {
      if (Array.isArray(messages) && messages.length > 0) {
        parsed[field] = messages[0];
      } else if (typeof messages === 'string') {
        parsed[field] = messages;
      }

      // Expandir a seção que contém o campo com erro
      const section = FIELD_SECTION_MAP[field];
      if (section) sectionsToExpand.add(section);
    }

    if (Object.keys(parsed).length === 0) return false;

    setFieldErrors(parsed);

    // Única seção colapsável é "Origem & Fonte" — expandir se algum campo dela errou
    if (sectionsToExpand.has('origin')) setOriginExpanded(true);

    return true;
  };

  // ─── Limpar erro de um campo específico ao alterar valor ───
  const clearFieldError = (field: string) => {
    if (fieldErrors[field]) {
      setFieldErrors(prev => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  // ─── Submit ───
  const handleSubmit = async () => {
    setFieldErrors({});

    if (isEdit) {
      // ── Edit mode ──
      if (!lead) return;

      try {
        setIsLoading(true);
        await leadsService.updateLead(lead.id, {
          funnelId: selectedFunnelId || undefined,
          stepId: selectedStepId || undefined,
          priority: (priority as any) || undefined,
          temperature: (temperature as any) || undefined,
          qualifyStatus: (qualifyStatus as any) || undefined,
          channel: (channel as any) || undefined,
          origin: (origin as any) || undefined,
          source: (source as any) || undefined,
          medium: (medium as any) || undefined,
          type: type.trim() || undefined,
          campaignName: campaignName.trim() || undefined,
          adsetName: adsetName.trim() || undefined,
          adName: adName.trim() || undefined,
          assigneeId: assigneeId || undefined,
          teamId: teamId || undefined,
          budget,
          segment: segment.trim() || undefined,
          description: description.trim() || undefined,
        }, { handleErrorLocally: true });

        toast.success('Oportunidade atualizada com sucesso!');
        onSuccess?.();
        onClose();
      } catch (error) {
        console.error('Erro ao editar oportunidade:', error);
        if (!parseBackendErrors(error)) {
          toast.error('Erro ao editar oportunidade. Tente novamente.');
        }
      } finally {
        setIsLoading(false);
      }
    } else {
      // ── Create mode ──
      if (!selectedContact || !selectedFunnelId || !selectedStepId) return;

      try {
        setIsLoading(true);
        await createLeadMutation.mutateAsync({
          contactId: selectedContact.id,
          segment: segment.trim() || '',
          funnelId: selectedFunnelId,
          stepId: selectedStepId,
          assigneeId: assigneeId || undefined,
          teamId: teamId || undefined,
          budget,
          priority: (priority as any) || undefined,
          description: description.trim() || undefined,
          temperature: (temperature as any) || undefined,
          qualifyStatus: (qualifyStatus as any) || undefined,
          channel: (channel as any) || undefined,
          origin: (origin as any) || undefined,
          source: (source as any) || undefined,
          medium: (medium as any) || undefined,
          type: type.trim() || undefined,
          campaignName: campaignName.trim() || undefined,
          adsetName: adsetName.trim() || undefined,
          adName: adName.trim() || undefined,
          _handleErrorLocally: true,
        } as any);

        toast.success('Oportunidade criada com sucesso!');
        onSuccess?.();
        onClose();
      } catch (error) {
        console.error('Erro ao criar oportunidade:', error);
        if (!parseBackendErrors(error)) {
          toast.error('Erro ao criar oportunidade. Tente novamente.');
        }
      } finally {
        setIsLoading(false);
      }
    }
  };

  // ─── Options ───
  const funnelOptions = (funnelsData || []).map(f => ({ value: f.id, label: f.name }));
  const stepOptions = (steps || []).map(s => ({ value: s.id, label: s.name }));
  const userOptions = (assignableUsers ?? []).map(u => ({ value: u.id, label: u.name }));
  if (keepsOriginalAssignee && assignableUsers && !userOptions.some(o => o.value === originalAssigneeId)) {
    userOptions.push({ value: originalAssigneeId, label: `${lead?.assignee?.name ?? 'Responsável atual'} (fora do funil)` });
  }
  const teamOptions = (teamsData || []).map((t: { id: string; name: string }) => ({ value: t.id, label: t.name }));
  const campaignOptions = (campaignsData || []).map(c => ({ value: c, label: c }));
  const adsetOptions = (adsetsData || []).map(a => ({ value: a, label: a }));
  const adOptions = (adsData || []).map(a => ({ value: a, label: a }));

  // ─── Validation ───
  const isContactValid = isEdit || selectedContact !== null;
  const isFunnelValid = selectedFunnelId !== '';
  const isStepValid = selectedStepId !== '';
  const canSubmit = isContactValid && isFunnelValid && isStepValid && !isLoading;

  // ─── Contact info for edit mode ───
  const contactName = lead?.contact?.name || 'Sem nome';
  const contactEmail = lead?.contact?.identifiers?.email?.[0] || lead?.contact?.email;
  const contactPhone = lead?.contact?.identifiers?.phone?.[0] || lead?.contact?.phone;

  return (
    <>
      <Drawer
        isOpen={isOpen}
        onClose={onClose}
        size="xl"
        icon={<Icon name="briefcase" className="w-4 h-4" />}
        title={isEdit ? 'Editar Oportunidade' : 'Nova Oportunidade'}
        subtitle={isEdit ? contactName : 'Preencha os dados e crie o lead no funil'}
        disabled={isLoading}
        footer={
          <>
            {/* `.b-ghost` do mockup: borda #e2e6ec, texto #475569 */}
            <Button
              onClick={onClose}
              variant="ghost"
              size="md"
              disabled={isLoading}
              style={{ color: '#475569' }}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleSubmit}
              variant="primary"
              size="md"
              icon="check"
              disabled={!canSubmit}
            >
              {isLoading
                ? (isEdit ? 'Salvando...' : 'Criando...')
                : (isEdit ? 'Salvar' : 'Criar')
              }
            </Button>
          </>
        }
      >
        {/* `.drw-body` do lead-form: seções `.ld-sec` carregam o próprio padding */}
        <div>
          {/* ═══ 1 · Contato * (não colapsável) ═══ */}
          <Sec first>
            <SecH title="Contato" icon="user" required />
            {(isEdit || selectedContact) ? (
              /* `.contact-chip` do mockup: sunken, av 46, nome 14 bold, email·fone 12,
                 mini-btn "Editar contato" + × (limpar — só no create) */
              <div
                className="flex items-center gap-3 rounded-lg border"
                style={{ padding: 12, backgroundColor: '#f1f5f9', borderColor: 'rgba(0,0,0,.10)' }}
                data-testid="lead-form-contact-chip"
              >
                <div
                  className="flex items-center justify-center flex-shrink-0 text-white font-semibold"
                  style={{ width: 46, height: 46, borderRadius: '32%', backgroundColor: '#64748b', fontSize: 16 }}
                >
                  {(isEdit ? contactName : selectedContact?.name || '?')
                    .trim().split(/\s+/).map((w) => w[0]).slice(0, 2).join('').toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold truncate" style={{ color: '#1e293b' }}>
                    {isEdit ? contactName : selectedContact?.name}
                  </p>
                  <p className="text-xs truncate" style={{ color: '#6b7280', marginTop: 2 }}>
                    {[
                      isEdit ? contactEmail : selectedContact?.email,
                      isEdit ? contactPhone : selectedContact?.phone,
                    ].filter(Boolean).join(' · ') || 'Sem dados de contato'}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setIsEditContactOpen(true)}
                  className="inline-flex items-center gap-[5px] flex-none font-semibold rounded-[7px] border transition-colors hover:bg-white"
                  style={{ height: 30, padding: '0 11px', fontSize: 12, borderColor: 'rgba(0,0,0,.10)', color: '#334155', backgroundColor: '#ffffff' }}
                  data-testid="lead-form-contact-edit"
                >
                  <Icon name="edit" className="w-3.5 h-3.5" />
                  Editar contato
                </button>
                {!isEdit && (
                  <button
                    type="button"
                    onClick={() => setSelectedContact(null)}
                    className="flex items-center justify-center flex-none rounded-[9px] transition-colors"
                    style={{ width: 34, height: 34, color: '#6b7280' }}
                    onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'rgba(0,0,0,.05)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                    title="Limpar contato"
                    data-testid="lead-form-contact-clear"
                  >
                    <Icon name="x" className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            ) : (
              /* Searchable combobox (estado vazio) */
              <ContactSearchCombobox
                value={selectedContact}
                onChange={setSelectedContact}
                onRequestCreateContact={handleRequestCreateContact}
                disabled={isLoading}
                error={!isContactValid && selectedFunnelId ? 'Selecione ou crie um contato' : undefined}
              />
            )}
          </Sec>

          {/* ═══ 2 · Funil e etapa * (não colapsável) ═══ */}
          <Sec>
            <SecH title="Funil e etapa" icon="trending-up" required />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
              <Select
                label="Funil"
                required
                value={selectedFunnelId}
                onChange={(v) => { setSelectedFunnelId(v); clearFieldError('funnelId'); }}
                placeholder="Selecione"
                options={funnelOptions}
                disabled={isLoading}
                error={fieldErrors.funnelId}
              />
              <Select
                label="Etapa"
                required
                value={selectedStepId}
                onChange={(v) => { setSelectedStepId(v); clearFieldError('stepId'); }}
                placeholder={!selectedFunnelId ? 'Selecione um funil' : 'Selecione'}
                options={stepOptions}
                disabled={isLoading || !selectedFunnelId}
                error={fieldErrors.stepId}
              />
            </div>
          </Sec>

          {/* ═══ 3 · Atribuição (colapsável, expandida) ═══ */}
          <Sec>
            <SecH title="Atribuição" icon="user-check" collapsible open={assignExpanded} onToggle={() => setAssignExpanded(v => !v)} />
            {assignExpanded && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
              <Select
                label="Responsável"
                value={assigneeId}
                onChange={(v) => { setAssigneeId(v); clearFieldError('assigneeId'); }}
                placeholder={selectedFunnelId ? 'Selecione' : 'Escolha o funil primeiro'}
                options={userOptions}
                disabled={isLoading || !selectedFunnelId}
                error={fieldErrors.assigneeId}
              />
              <Select
                label="Equipe"
                value={teamId}
                onChange={(v) => { setTeamId(v); clearFieldError('teamId'); }}
                placeholder="Selecione"
                options={teamOptions}
                disabled={isLoading}
                error={fieldErrors.teamId}
              />
            </div>
            )}
          </Sec>

          {/* ═══ 4 · Classificação (colapsável, expandida; row3) ═══ */}
          <Sec>
            <SecH title="Classificação" icon="star" collapsible open={classifyExpanded} onToggle={() => setClassifyExpanded(v => !v)} />
            {classifyExpanded && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3.5">
              <Select
                label="Prioridade"
                value={priority}
                onChange={(v) => { setPriority(v); clearFieldError('priority'); }}
                placeholder="Selecione"
                options={PRIORITY_OPTIONS}
                disabled={isLoading}
                error={fieldErrors.priority}
              />
              <Select
                label="Temperatura"
                value={temperature}
                onChange={(v) => { setTemperature(v); clearFieldError('temperature'); }}
                placeholder="Selecione"
                options={TEMPERATURE_OPTIONS}
                disabled={isLoading}
                error={fieldErrors.temperature}
              />
              <Select
                label="Qualificação"
                value={qualifyStatus}
                onChange={(v) => { setQualifyStatus(v); clearFieldError('qualifyStatus'); }}
                placeholder="Selecione"
                options={QUALIFY_STATUS_OPTIONS}
                disabled={isLoading}
                error={fieldErrors.qualifyStatus}
              />
            </div>
            )}
          </Sec>

          {/* ═══ 5 · Detalhes (colapsável, expandida; grid2 Orçamento/Segmento + Descrição) ═══ */}
          <Sec>
            <SecH title="Detalhes" icon="file-text" collapsible open={detailsExpanded} onToggle={() => setDetailsExpanded(v => !v)} />
            {detailsExpanded && (
            <SecBody>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                <MoneyInput
                  label="Orçamento"
                  value={budget}
                  onChange={(v) => { setBudget(v); clearFieldError('budget'); }}
                  placeholder="0,00"
                  disabled={isLoading}
                  hideHelperText
                  error={fieldErrors.budget}
                />
                <div>
                  <label className="block text-[13px] font-semibold mb-1.5" style={{ color: '#334155' }}>
                    Segmento
                  </label>
                  <Input
                    type="text"
                    value={segment}
                    onChange={(e) => { setSegment(e.target.value); clearFieldError('segment'); }}
                    placeholder="Ex: Tecnologia, Varejo..."
                    disabled={isLoading}
                    error={fieldErrors.segment}
                  />
                </div>
              </div>
              <Textarea
                label="Descrição"
                value={description}
                onChange={(e) => { setDescription(e.target.value); clearFieldError('description'); }}
                placeholder="Adicione uma descrição para esta oportunidade..."
                rows={3}
                resize="vertical"
                disabled={isLoading}
                error={fieldErrors.description}
              />
            </SecBody>
            )}
          </Sec>

          {/* ═══ 6 · Origem & Fonte (colapsável, FECHADA por default — mockup) ═══ */}
          <Sec>
            <SecH title="Origem & Fonte" icon="globe" collapsible open={originExpanded} onToggle={() => setOriginExpanded(v => !v)} />
            {originExpanded && (
              /* Arranjo do mockup lead-form §6: (Fonte|Canal) → (Meio|Tipo) →
                 Origem full → (Campanha|Conjunto de Anúncio) → Anúncio full */
              <div className="space-y-3">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                  <Select
                    label="Fonte"
                    value={source}
                    onChange={(v) => { setSource(v); setChannel(''); clearFieldError('source'); }}
                    placeholder="Selecione"
                    options={SOURCE_OPTIONS}
                    disabled={isLoading}
                    error={fieldErrors.source}
                  />
                  <Select
                    label="Canal"
                    value={channel}
                    onChange={(v) => { setChannel(v); clearFieldError('channel'); }}
                    placeholder="Selecione"
                    options={source && SOURCE_CHANNEL_MAP[source] ? SOURCE_CHANNEL_MAP[source] : CHANNEL_OPTIONS}
                    disabled={isLoading}
                    error={fieldErrors.channel}
                  />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                  <Select
                    label="Meio"
                    value={medium}
                    onChange={(v) => { setMedium(v); clearFieldError('medium'); }}
                    placeholder="Selecione"
                    options={MEDIUM_OPTIONS}
                    disabled={isLoading}
                    error={fieldErrors.medium}
                  />
                  <Select
                    label="Tipo"
                    value={type}
                    onChange={(v) => { setType(v); }}
                    placeholder={(() => {
                      if (!selectedFunnelId) return 'Selecione um funil primeiro';
                      const selectedFunnel = funnelsData?.find(f => f.id === selectedFunnelId);
                      const funnelTypes = selectedFunnel?.types || [];
                      return funnelTypes.length > 0 ? 'Selecione' : 'Nenhum tipo cadastrado';
                    })()}
                    options={(() => {
                      const selectedFunnel = funnelsData?.find(f => f.id === selectedFunnelId);
                      const funnelTypes = selectedFunnel?.types || [];
                      return funnelTypes.map((t: string) => ({ value: t, label: t }));
                    })()}
                    disabled={isLoading || !selectedFunnelId}
                  />
                </div>
                <div>
                  <label className="block text-[13px] font-semibold mb-1.5" style={{ color: '#334155' }}>
                    Origem
                  </label>
                  <Input
                    type="text"
                    value={origin}
                    onChange={(e) => { setOrigin(e.target.value); clearFieldError('origin'); }}
                    placeholder="Ex: @perfil, Citalli..."
                    disabled={isLoading}
                    error={fieldErrors.origin}
                  />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                  <Select
                    label="Campanha"
                    value={campaignName}
                    onChange={(v) => { setCampaignName(v); clearFieldError('campaignName'); }}
                    placeholder="Selecione"
                    options={campaignOptions}
                    disabled={isLoading}
                    error={fieldErrors.campaignName}
                  />
                  <Select
                    label="Conjunto de Anúncio"
                    value={adsetName}
                    onChange={(v) => { setAdsetName(v); clearFieldError('adsetName'); }}
                    placeholder="Selecione"
                    options={adsetOptions}
                    disabled={isLoading}
                    error={fieldErrors.adsetName}
                  />
                </div>
                <Select
                  label="Anúncio"
                  value={adName}
                  onChange={(v) => { setAdName(v); clearFieldError('adName'); }}
                  placeholder="Selecione"
                  options={adOptions}
                  disabled={isLoading}
                  error={fieldErrors.adName}
                />
              </div>
            )}
          </Sec>
        </div>
      </Drawer>

      {/* AddContactDialog — renderizado FORA do Drawer para z-index correto */}
      {!isEdit && (
        <AddContactDialog
          isOpen={isAddContactOpen}
          onClose={() => setIsAddContactOpen(false)}
          onSave={handleSaveNewContact}
          isLoading={createContactMutation.isPending}
        />
      )}

      {/* EditContactDialog — renderizado FORA do Drawer para z-index correto */}
      <EditContactDialog
        isOpen={isEditContactOpen && !!fullContact}
        onClose={() => setIsEditContactOpen(false)}
        onSave={handleUpdateContact}
        contact={fullContact ?? null}
        isLoading={updateContactMutation.isPending}
      />
    </>
  );
};

LeadFormDrawer.displayName = 'LeadFormDrawer';
